import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EligibilityPill } from "@/components/EligibilityPill";
import { MatchScore } from "@/components/MatchScore";
import { formatSalary, locationLabel, arrangementLabel } from "@/lib/format";
import { RemoteScopeBadge } from "@/components/RemoteScope";
import { Button } from "@/components/ui/button";
import { ArrowRight, Warning } from "@phosphor-icons/react";

const STATS = [
  { key: "eligible", label: "Eligible", cls: "text-emerald-700" },
  { key: "possibly_eligible", label: "Possibly eligible", cls: "text-orange-700" },
  { key: "applications", label: "Applications", cls: "text-slate-900" },
  { key: "saved_jobs", label: "Saved jobs", cls: "text-slate-900" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recs, setRecs] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/recommendations").then((r) => setRecs(r.data.jobs));
  }, []);

  return (
    <div>
      <PageHeader
        title={`Welcome${user?.name ? ", " + user.name.split(" ")[0] : ""}`}
        subtitle="Your job search, filtered by what you can realistically apply for."
        action={<Link to="/jobs"><Button data-testid="dashboard-find-jobs-btn" className="bg-slate-900 hover:bg-slate-800 gap-2">Find jobs <ArrowRight weight="bold" /></Button></Link>}
      />

      {stats && !stats.profile_completed && (
        <div className="mb-6 flex items-start gap-3 rounded-md border border-orange-200 bg-orange-50 px-4 py-3" data-testid="profile-incomplete-banner">
          <Warning weight="fill" className="text-orange-600 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-orange-900">Complete your Career fit assessment for accurate eligibility</p>
            <p className="text-orange-800">A short guided assessment sets your constraints and preferences — then you can import your background from a CV. <Link to="/career-fit" className="underline font-medium">Start assessment</Link></p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {STATS.map((s) => (
          <div key={s.key} className="rounded-lg border border-slate-200 bg-white p-5" data-testid={`stat-${s.key}`}>
            <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">{s.label}</p>
            <p className={`mt-2 font-mono-num font-semibold text-3xl ${s.cls}`}>{stats ? stats[s.key] : "—"}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-bold text-lg">Recommended for you</h2>
        <Link to="/jobs" className="text-sm font-medium text-slate-700 hover:text-slate-900 flex items-center gap-1">View all <ArrowRight /></Link>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="recommended-list">
        {recs.length === 0 && <p className="p-6 text-sm text-slate-500">No recommendations yet. Complete your profile to see matched roles.</p>}
        {recs.map((j) => (
          <Link key={j.id} to={`/jobs/${j.id}`}
            className="flex items-center gap-4 px-5 py-4 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors"
            data-testid={`rec-job-${j.id}`}>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-slate-900 truncate">{j.title}</p>
              <p className="text-sm text-slate-600 truncate">{j.employer} · {locationLabel(j)} · {arrangementLabel(j.work_arrangement)}{formatSalary(j.salary_min, j.salary_max, j.salary_currency) ? " · " + formatSalary(j.salary_min, j.salary_max, j.salary_currency) : ""}</p>
              <div className="mt-1"><RemoteScopeBadge summary={j.remote_summary} /></div>
            </div>
            <EligibilityPill status={j.eligibility_status} />
            <div className="w-14 text-right"><MatchScore score={j.match_score} /></div>
          </Link>
        ))}
      </div>
    </div>
  );
}
